import { Can } from '@/components/can/Can';
import { Form, useForm } from '@/components/forms';
import { Stepper } from '@/components/Stepper';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { resolveUpload } from '@/lib/resolveUpload';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { SNS } from '../Sns';
import { BasicInfo } from './BasicInfo';
import { ClubInfo } from './ClubInfo';
import { ClubAddress } from './form/ClubAddress';
import { ClubInfo as ClubInfoForm } from './form/ClubInfo';
import { Others } from './form/Others';
import { usePremiumStoreDetails } from './hooks/useGetStoreDetails';
import useManagePremiumStoreInfo from './hooks/useManagePremiumStore';
import { formSchema } from './premiumStore.schema';
import { STORE_SETUP_STEPS } from './premiumStores.constant';
const DEFAULT_STEP = 1;

export const ManagePremiumStores = () => {
  const [activeStep, setActiveStep] = useState(DEFAULT_STEP);
  const [profileImage, setProfileImage] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [passportImage, setPassportImage] = useState('');
  const [insuranceImage, setInsuranceImage] = useState('');
  const [businessImage, setBusinessImage] = useState('');
  const [corporateRegistryImage, setCorporateRegistryImage] = useState('');
  const [kycFrontImage, setKycFrontImage] = useState('');
  const [kycBackImage, setKycBackImage] = useState('');
  const { getPremiumStoreDetails, isLoading } = usePremiumStoreDetails();
  const params = useParams({ from: '/(app)/store-information/premium-stores/$manage-premium-store' });
  const premiumStoreId = params['manage-premium-store'];

  const { mutateAsync, isPending: isSubmitting } = useManagePremiumStoreInfo();

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    defaultValues: {},
  });

  useEffect(() => {
    if (!getPremiumStoreDetails) return;
    if (isLoading) return;

    if (Object.keys(getPremiumStoreDetails).length === 0) return;

    form.reset({
      profileImage: getPremiumStoreDetails.profileImage.path || '',
      coverImage: getPremiumStoreDetails.coverImage.path || '',
      storeName: getPremiumStoreDetails.storeName || '',
      storeNameFurigana: getPremiumStoreDetails.storeNameFurigana || '',
      groupName: getPremiumStoreDetails.groupName || '',
      description: getPremiumStoreDetails.description || '',
      ceoFullName: getPremiumStoreDetails.ceoFullName || '',
      ceoFurigana: getPremiumStoreDetails.ceoFurigana || '',
      companyName: getPremiumStoreDetails.companyName || '',
      companyNameFurigana: getPremiumStoreDetails.companyNameFurigana || '',
      companyAddress: getPremiumStoreDetails.companyAddress || '',
      landlineNumber: getPremiumStoreDetails.landlineNumber || '',
      mobileNumber: getPremiumStoreDetails.mobileNumber || '',
      emailAddress: getPremiumStoreDetails.emailAddress || '',
      username: getPremiumStoreDetails.username || '',
      businessType: getPremiumStoreDetails.businessType || '',
      identificationType: getPremiumStoreDetails.identificationType || '',
      corporateRegistryImage: getPremiumStoreDetails.corporateRegistryImage.path || '',
      kycFront: getPremiumStoreDetails.kycFrontImage.path || '',
      kycBack: getPremiumStoreDetails.kycFrontImage.path || '',
      insuranceCardImage: getPremiumStoreDetails.insuranceCardImage.path || '',
      passportImage: getPremiumStoreDetails.passportImage.path || '',
      businessCertificateImage: getPremiumStoreDetails.businessCertificateImage.path || '',
      businessLicenseNumber: getPremiumStoreDetails.businessLicenseNumber || '',
      licenseIssueDate: getPremiumStoreDetails.licenseIssueDate
        ? new Date(getPremiumStoreDetails.licenseIssueDate).toISOString()
        : '',
      longitude: getPremiumStoreDetails.longitude || '',
      latitude: getPremiumStoreDetails.latitude || '',
      postalCode: getPremiumStoreDetails.postalCode || '',
      prefecture: getPremiumStoreDetails.prefecture || '',
      city: getPremiumStoreDetails.city || '',
      street: getPremiumStoreDetails.street || '',
      buildingNo: getPremiumStoreDetails.buildingNo || '',
      workingTimeFrom: getPremiumStoreDetails.workingTimeFrom || '',
      workingTimeTo: getPremiumStoreDetails.workingTimeTo || '',
      lastEntryTime: getPremiumStoreDetails.lastEntryTime || '',
      holidays: getPremiumStoreDetails.holidays || '',
      otherHolidays: getPremiumStoreDetails.otherHolidays || [],
      tax: getPremiumStoreDetails.tax || '',
      closingDate: getPremiumStoreDetails.closingDate || '',
      instagram: getPremiumStoreDetails.instagram || '',
      tiktok: getPremiumStoreDetails.tiktok || '',
      line: getPremiumStoreDetails.line || '',
      twitter: getPremiumStoreDetails.twitter || '',
      website: getPremiumStoreDetails.website || '',
      googleMap: getPremiumStoreDetails.googleMap || '',
    });
    setProfileImage(getPremiumStoreDetails?.profileImage?.path);
    setCoverImage(getPremiumStoreDetails?.coverImage?.path);
    setPassportImage(getPremiumStoreDetails?.passportImage?.path);
    setKycFrontImage(getPremiumStoreDetails?.kycFrontImage?.path);
    setKycBackImage(getPremiumStoreDetails?.kycBackImage?.path);
    setInsuranceImage(getPremiumStoreDetails?.insuranceCardImage?.path);
    setBusinessImage(getPremiumStoreDetails?.businessCertificateImage?.path);
    setCorporateRegistryImage(getPremiumStoreDetails?.corporateRegistryImage?.path);
  }, [getPremiumStoreDetails]);
  const businessTypeFromApi = getPremiumStoreDetails?.businessType;
  const identificationTypeFromApi = getPremiumStoreDetails?.identificationType;
  const prefectureFromApi = getPremiumStoreDetails?.prefecture;
  const workingHourFromApi = getPremiumStoreDetails?.workingTimeFrom;
  const workingHourToApi = getPremiumStoreDetails?.workingTimeTo;
  const holidaysFromApi = getPremiumStoreDetails?.holidays;
  const closingDateFromApi = getPremiumStoreDetails?.closingDate;
  const lastEntryFromApi = getPremiumStoreDetails?.lastEntryTime;
  const { t } = useDynamicLocale();
  const handleNext = async () => {
    const formData = form.getValues();
    const stepConfig = STORE_SETUP_STEPS.find((s) => s.step === activeStep);

    const isValid = await form.trigger(stepConfig?.fields, { shouldFocus: true });

    const profileImageFile = formData.profileImage?.valueOf();
    const coverImageFile = formData.coverImage?.valueOf();

    if (!profileImageFile) {
      toast.error(t('common.form.validation.profileUpload'));
      return;
    }
    if (!coverImageFile) {
      toast.error(t('common.form.validation.coverUpload'));
      return;
    }

    if (!isValid) return;

    setActiveStep((prev) => prev + 1);
  };

  return (
    <div className="rounded-lg bg-card shadow-panel p-8">
      <Stepper
        items={STORE_SETUP_STEPS}
        value={activeStep}
        defaultActive={DEFAULT_STEP}
        onValueChange={setActiveStep}
      />
      <Form
        form={form}
        onSubmit={async () => {
          try {
            const formData = form.getValues();

            const [
              profileImageUrl,
              coverImageUrl,
              passportImageUrl,
              insuranceCardImageUrl,
              businessCertificateImageUrl,
              corporateRegistryImageUrl,
              kycFrontUrl,
              kycBackUrl,
            ] = await Promise.all([
              resolveUpload(formData.profileImage),
              resolveUpload(formData.coverImage),
              resolveUpload(formData.passportImage),
              resolveUpload(formData.insuranceCardImage),
              resolveUpload(formData.businessCertificateImage),
              resolveUpload(formData.corporateRegistryImage),
              resolveUpload(formData.kycFront),
              resolveUpload(formData.kycBack),
            ]);

            const payload = {
              id: premiumStoreId || '',
              profileImage: profileImageUrl || '',
              coverImage: coverImageUrl || '',
              storeName: formData.storeName || '',
              storeNameFurigana: formData.storeNameFurigana || '',
              groupName: formData.groupName || '',
              description: formData.description || '',
              businessType: formData.businessType || '',
              ceoFullName: formData.ceoFullName || '',
              ceoFurigana: formData.ceoFurigana || '',
              companyName: formData.companyName || '',
              companyNameFurigana: formData.companyNameFurigana || '',
              companyAddress: formData.companyAddress || '',
              landlineNumber: formData.landlineNumber || '',
              mobileNumber: formData.mobileNumber || '',
              emailAddress: formData.emailAddress || '',
              username: formData.username || '',
              identificationType: formData.identificationType || '',
              passportImage: passportImageUrl || '',
              insuranceCardImage: insuranceCardImageUrl || '',
              businessCertificateImage: businessCertificateImageUrl || '',
              kycFrontImage: kycFrontUrl || '',
              kycBackImage: kycBackUrl || '',
              corporateRegistryImage: corporateRegistryImageUrl || '',
              businessLicenseNumber: formData.businessLicenseNumber || '',
              licenseIssueDate: formData.licenseIssueDate ? new Date(formData.licenseIssueDate).getTime() : 0,
              longitude: formData.longitude || '',
              latitude: formData.latitude || '',
              postalCode: formData.postalCode || '',
              prefecture: formData.prefecture || '',
              city: formData.city || '',
              street: formData.street || '',
              buildingNo: formData.buildingNo || '',
              workingTimeFrom: formData.workingTimeFrom || '',
              workingTimeTo: formData.workingTimeTo || '',
              lastEntryTime: formData.lastEntryTime || '',
              holidays: formData.holidays || '',
              tax: formData.tax || '',
              closingDate: formData.closingDate || '',
              otherHolidays: formData.otherHolidays || [],
              instagram: formData.instagram || '',
              tiktok: formData.tiktok || '',
              line: formData.line || '',
              twitter: formData.twitter || '',
              website: formData.website || '',
              googleMap: formData.googleMap || '',
            };

            await mutateAsync(payload);
          } catch {
            // The mutation displays the user-facing submission error.
          }
        }}
      >
        <fieldset disabled={!getPremiumStoreDetails || isLoading}>
          <div className={activeStep === 1 ? 'block' : 'hidden'}>
            <BasicInfo
              premiumStoreId={premiumStoreId}
              businessTypeFromApi={businessTypeFromApi}
              identificationTypeFromApi={identificationTypeFromApi}
              profileImagePath={profileImage}
              setProfileImage={setProfileImage}
              coverImagePath={coverImage}
              setCoverImage={setCoverImage}
              kycFrontImage={kycFrontImage}
              setKycFrontImage={setKycFrontImage}
              kycBackImage={kycBackImage}
              setKycBackImage={setKycBackImage}
              passportImagePath={passportImage}
              setPassportImage={setPassportImage}
              insuranceImagePath={insuranceImage}
              setInsuranceImage={setInsuranceImage}
              businessImagePath={businessImage}
              setBusinessImage={setBusinessImage}
              corporateRegistryImagePath={corporateRegistryImage}
              setCorporateRegistryImage={setCorporateRegistryImage}
            />
          </div>
          <div className={activeStep === 2 ? 'block' : 'hidden'}>
            <ClubInfo>
              <div className="w-full flex flex-col gap-6 mt-8">
                <ClubAddress premiumStoreId={premiumStoreId} prefectureFromApi={prefectureFromApi} />
                <ClubInfoForm
                  workingHourFromApi={workingHourFromApi}
                  workingHourToApi={workingHourToApi}
                  closingDateFromApi={closingDateFromApi}
                  holidaysFromApi={holidaysFromApi}
                  lastEntryFromApi={lastEntryFromApi}
                />
                <SNS storeType="premium" />
                <Others />
              </div>
            </ClubInfo>
          </div>
          <div className="flex w-full gap-4 text-sm mt-6">
            {activeStep === 2 ? (
              <div className="flex justify-between w-full">
                <Link to="/store-information/premium-stores">
                  <Button type="button" variant="outline" className="w-[122px]" disabled={isSubmitting}>
                    <Translation id="common.page.button.cancel" />
                  </Button>
                </Link>
                <div className="flex flex-row-reverse gap-4">
                  <Can rule={{ allOf: [PERMS.club.premium.update] }}>
                    <Button type="submit" className="w-[110px]" disabled={isSubmitting}>
                      <Translation id="common.page.button.submit" />
                    </Button>
                  </Can>
                  <Button
                    variant="outline"
                    className="w-[122px]"
                    disabled={isSubmitting}
                    onClick={() => setActiveStep(activeStep - 1)}
                  >
                    <Translation id="common.page.button.previous" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" className="w-[110px] ml-auto" onClick={() => void handleNext()}>
                <Translation id="common.page.button.next" />
              </Button>
            )}
          </div>
        </fieldset>
      </Form>
    </div>
  );
};
